import { signal } from "@preact/signals";
import { getThemePref, setThemePref, type ThemePref } from "./storage.js";

/**
 * Light and dark are both first-class here.
 *
 * Dark is the app's signature look, but this gets used on a fairway in full sun,
 * where a dark screen is genuinely hard to read — so the light theme is a real
 * design, not an afterthought, and the default follows whatever the phone says.
 *
 * The same preference is read by an inline script in index.html before first paint,
 * so a dark load never flashes white. Both must agree on the storage key
 * ("golf:theme", owned by storage.ts) and on these two colours.
 */
const BAR_COLOR: Record<Theme, string> = { dark: "#0A0F0C", light: "#F6F8F6" };

export type Theme = "light" | "dark";

export const themePref = signal<ThemePref>(getThemePref());

function systemPrefersDark(): boolean {
  return typeof window !== "undefined" && Boolean(window.matchMedia?.("(prefers-color-scheme: dark)").matches);
}

export function resolvedTheme(): Theme {
  const pref = themePref.value;
  if (pref === "system") return systemPrefersDark() ? "dark" : "light";
  return pref;
}

function apply() {
  const theme = resolvedTheme();
  document.documentElement.setAttribute("data-theme", theme);
  document.getElementById("theme-color")?.setAttribute("content", BAR_COLOR[theme]);
}

/**
 * Flips to the opposite of what's on screen right now. That makes the toggle mean
 * exactly what it looks like it means; the cost is that there's no way back to
 * "follow the phone" once you've touched it, which is the usual trade and the
 * right one for a control this small.
 */
export function toggleTheme() {
  const next: ThemePref = resolvedTheme() === "dark" ? "light" : "dark";
  themePref.value = next;
  setThemePref(next);
  apply();
}

/** While the preference is still "system", track the phone changing its mind. */
export function watchSystemTheme() {
  const mq = window.matchMedia?.("(prefers-color-scheme: dark)");
  mq?.addEventListener?.("change", () => {
    if (themePref.value === "system") apply();
  });
  apply();
}
